import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { salvarArquivoDoFinanceiro } from '@/features/operations/financeiroDaCotacao';
import {
  policyholderFile,
  type ConsultationDataSection,
  type CreditConsultation,
  type PolicyholderAutomaticAnalysis,
  type PolicyholderRunRule,
  type ScoreBand,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { useToast } from '@/shared/ui/useToast';

import { rotuloDoResultadoDoMotor } from '../rotulos';
import { corDaRegra } from './coresDaAnalise';
import { MedidoresDeScore } from './MedidorDeScore';
import { QuadrosDaConsulta } from './QuadrosDaConsulta';
import { Bloco, Info, InfoGrid } from './shared';
import { textoDoValorDaConsulta as texto } from './valorDaConsulta';

/**
 * Analise automatica do tomador na secao Analise de Credito (ADR 0090): o que a ultima
 * consulta trouxe e o que o motor decidiu logo depois dela. Somente leitura: a consulta
 * sai pelos botoes da secao, e a decisao da pessoa, pela analise de credito do tomador.
 */
export function AnaliseAutomaticaDoTomador({
  tomadorId,
  documento,
}: {
  tomadorId: string;
  documento?: string;
}) {
  const { can } = useAuth();
  const analise = useQuery({
    queryKey: ['policyholder', tomadorId, 'automatic-credit-analysis'],
    queryFn: ({ signal }) => policyholderFile.automaticAnalysis(tomadorId, signal),
    enabled: can('credito.motor.view'),
  });

  if (!can('credito.motor.view')) return null;
  if (analise.isPending) {
    return <p className="text-sm text-ink-500">Carregando a analise automatica...</p>;
  }
  if (analise.error) {
    return (
      <Alert tone="danger" title="Analise automatica indisponivel">
        {analise.error.message}
      </Alert>
    );
  }
  const dados = analise.data;
  return (
    <div className="flex flex-col gap-6">
      <DadosDaConsulta
        consulta={dados.consultation}
        master={dados.master}
        faixas={dados.score_bands ?? []}
        elementoDaClasse={dados.score_element ?? ''}
        quadros={dados.data_sections ?? []}
      />
      <ResultadoDoMotor
        analise={dados}
        exportar={
          <BotaoExportarAnalise
            tomadorId={tomadorId}
            nome={`analise-de-credito-${documento ?? tomadorId}`}
          />
        }
      />
    </div>
  );
}

// --- Dados da ultima consulta -------------------------------------------------------

const ambientes: Record<string, string> = {
  producao: 'producao',
  teste: 'teste',
  simulado: 'simulado (bureau ficticio)',
};

function DadosDaConsulta({
  consulta,
  master,
  faixas,
  elementoDaClasse,
  quadros,
}: {
  consulta: CreditConsultation | null;
  master: CreditConsultation | null;
  faixas: ScoreBand[];
  elementoDaClasse: string;
  quadros: ConsultationDataSection[];
}) {
  if (!consulta) {
    return (
      <Bloco titulo="Dados da ultima consulta SeguraCoreCredit">
        <p className="text-sm text-ink-500">
          Nenhuma consulta com dados ainda. Depois de Consultar Restritivos, o que a
          SeguraCoreCredit trouxer aparece aqui, e o motor de credito roda sobre o
          tomador.
        </p>
      </Bloco>
    );
  }
  const score = (elemento: string) =>
    texto(consulta.values?.find((v) => v.element === elemento));
  const etapa = consulta.stage === 'master' ? 'Master' : 'basica';
  return (
    <Bloco titulo="Dados da ultima consulta SeguraCoreCredit">
      <p className="flex flex-wrap items-center gap-2 text-sm text-ink-700">
        <span>
          Consulta {etapa} de {formatDateTime(consulta.consulted_at)}, ambiente{' '}
          {ambientes[consulta.environment ?? ''] ?? consulta.environment ?? '-'}
          {consulta.valid ? '' : ', fora da validade'}.
          {master && master.id !== consulta.id
            ? ` Consulta Master de ${formatDateTime(master.consulted_at)}${master.valid ? '' : ', fora da validade'}.`
            : ''}
        </span>
        {consulta.simulated ? <Badge tone="warning">SIMULADO</Badge> : null}
      </p>
      {faixas.length > 0 ? (
        <MedidoresDeScore
          consulta={consulta}
          faixas={faixas}
          elementoDaClasse={elementoDaClasse}
        />
      ) : (
        <InfoGrid>
          <Info label="Score D00" value={score('vgd_score_d00')} />
          <Info label="Score D30" value={score('vgd_score_d30')} />
          <Info label="Score D60" value={score('vgd_score_d60')} />
        </InfoGrid>
      )}
      <QuadrosDaConsulta quadros={quadros} />
    </Bloco>
  );
}

// --- Resultado do motor e decisao ----------------------------------------------------

const resultadosDaDecisao: Record<string, string> = {
  aprovado: 'Credito do tomador aprovado pelo motor',
  esteira: 'Analise de credito aberta na esteira para uma pessoa decidir',
  anexado: 'Resultado anexado a analise de credito ja aberta',
  analise_desligada:
    'A empresa desligou a analise de credito do tomador: o resultado fica so na execucao',
  sem_cadastro: 'Decisao nao registrada nesta instalacao',
};

/**
 * Exporta a ultima analise gravada. O arquivo sai do servidor, que registra na trilha quem
 * o tirou. Sem conversor na instalacao, ele vem em Word, e a extensao segue o que chegou.
 */
function BotaoExportarAnalise({
  tomadorId,
  nome,
}: {
  tomadorId: string;
  nome: string;
}) {
  const toast = useToast();
  const exportar = useMutation({
    mutationFn: () => policyholderFile.automaticAnalysisFile(tomadorId),
    onSuccess: (arquivo) => {
      const extensao = arquivo.type === 'application/pdf' ? 'pdf' : 'docx';
      salvarArquivoDoFinanceiro(arquivo, `${nome}.${extensao}`);
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  // O icone do PDF com o texto ao lado: e a acao do quadro do resultado, e nao de uma
  // linha.
  return (
    <AcaoComIcone
      acao="baixarPdf"
      rotulo="Exportar PDF"
      texto
      loading={exportar.isPending}
      onClick={() => exportar.mutate()}
    />
  );
}

/**
 * O que aconteceu com a consulta Master nesta analise. "O motor nao pediu" nao e o mesmo
 * que "nao foi feita".
 */
function situacaoDaMaster(analise: PolicyholderAutomaticAnalysis): string {
  const { master, run } = analise;
  if (!master) {
    return run?.master_required
      ? 'Nao realizada. O motor pediu esta consulta.'
      : 'Nao realizada.';
  }
  const feita = `Realizada em ${formatDateTime(master.consulted_at)}`;
  if (!master.valid) {
    return `${feita}, vencida em ${formatDateTime(master.valid_until)}.${run?.master_required ? ' O motor pediu uma nova.' : ''}`;
  }
  return `${feita}, valida ate ${formatDateTime(master.valid_until)}. ${
    run?.master_used
      ? 'Regras da Master avaliadas nesta analise.'
      : 'Esta analise e anterior a ela.'
  }`;
}

function ResultadoDoMotor({
  analise,
  exportar,
}: {
  analise: PolicyholderAutomaticAnalysis;
  exportar: ReactNode;
}) {
  const run = analise.run;
  const decisao = analise.decision;
  const lista = analise.auto_approval_rules ?? [];
  const aviso = !analise.auto_approval_enabled
    ? 'A aprovacao automatica do tomador esta desligada: todo resultado vai para a esteira, para uma pessoa decidir.'
    : lista.length > 0
      ? `A aprovacao automatica do tomador esta ligada na versao ativa e decide pelas regras: ${lista.join(', ')}. Cada uma precisa seguir e estar tambem em regras_homologadas; as outras ficam registradas e nao seguram a aprovacao.`
      : 'A aprovacao automatica do tomador esta ligada na versao ativa: so o resultado elegivel, sem pendencia, e aprovado pelo motor.';
  if (!run) {
    return (
      <Bloco titulo="Analise automatica do motor">
        <p className="text-sm text-ink-500">
          O motor ainda nao rodou sobre este tomador. Ele roda logo depois de cada
          consulta nova ao bureau.
        </p>
        <p className="text-2xs text-ink-500">{aviso}</p>
      </Bloco>
    );
  }
  const elegivel = run.final_result === 'elegivel_aprovacao_automatica';
  return (
    <Bloco titulo="Analise automatica do motor" acao={exportar}>
      {decisao?.approve && decisao.result === 'aprovado' ? (
        <Alert tone="success" title="Aprovacao automatica">
          {decisao.note}. Decisao do {decisao.actor}; o limite e a taxa nao mudam.
        </Alert>
      ) : decisao ? (
        <Alert
          tone="info"
          title={resultadosDaDecisao[decisao.result] ?? decisao.result}
        >
          {decisao.held_back ? `${decisao.held_back} ` : ''}
          {decisao.reasons.length > 0
            ? `Por que nao foi aprovado sozinho: ${decisao.reasons.map((r) => r.message).join(' ')}`
            : null}
        </Alert>
      ) : null}
      <InfoGrid>
        <Info
          label="Resultado final"
          value={
            <Badge
              tone={
                elegivel
                  ? 'success'
                  : run.final_result === 'recusado'
                    ? 'danger'
                    : 'warning'
              }
            >
              {rotuloDoResultadoDoMotor(run.final_result)}
            </Badge>
          }
        />
        <Info
          label="Versao de parametros"
          value={`${run.parameter_set.version} (${run.parameter_set.status_at_run === 'ativa' ? 'ativa' : 'rascunho, em sombra'})`}
        />
        <Info label="Executado em" value={formatDateTime(run.created_at)} />
        <Info
          label="Regras"
          value={`${run.following_rules} seguem, ${run.rules_with_effect.length} com efeito, ${run.not_applicable} nao se aplicam ao tomador`}
        />
        <Info label="Consulta Master" value={situacaoDaMaster(analise)} />
        <Info label="Dado simulado" value={run.simulated ? 'Sim (SIMULADO)' : 'Nao'} />
      </InfoGrid>
      <p className="text-2xs text-ink-500">{aviso}</p>
      {decisao && (decisao.rules?.length ?? 0) > 0 ? (
        <RegrasDaDecisao
          regras={decisao.rules ?? []}
          foraDaLista={decisao.undecided ?? []}
        />
      ) : null}
      {run.rules_with_effect.length > 0 || (run.rules_following?.length ?? 0) > 0 ? (
        <p className="text-2xs text-ink-500">
          Verde: regra atendida. Vermelho: o dado foi contra a regra. Amarelo: faltou
          dado ou depende de uma pessoa.
        </p>
      ) : null}
      <ListaDeRegras
        titulo="Regras com efeito e pendencias"
        regras={run.rules_with_effect}
      />
      <ListaDeRegras titulo="Regras que seguiram" regras={run.rules_following ?? []} />
    </Bloco>
  );
}

/** As regras da execucao, com o resultado na cor dele. */
function ListaDeRegras({
  titulo,
  regras,
}: {
  titulo: string;
  regras: PolicyholderRunRule[];
}) {
  if (regras.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <h4 className="text-xs font-semibold text-ink-700">{titulo}</h4>
      <ul className="flex flex-col gap-1 text-sm">
        {regras.map((r) => (
          <li key={r.rule} className="flex flex-col">
            <span>
              <strong>{r.rule}</strong> {r.name}:{' '}
              <Badge tone={corDaRegra(r)}>
                {rotuloDoResultadoDoMotor(r.effective)}
              </Badge>
              {r.recommendation === 'recusa' ? ', com recomendacao de recusa' : ''}
              {r.shadow ? ' (regra nao homologada, em sombra)' : ''}
            </span>
            <span className="text-2xs text-ink-500">{r.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Quais regras decidiram a aprovacao automatica e quais ficaram de fora. */
function RegrasDaDecisao({
  regras,
  foraDaLista,
}: {
  regras: string[];
  foraDaLista: { rule: string; outcome: string; message: string }[];
}) {
  return (
    <div className="flex flex-col gap-1 text-sm">
      <p>
        <strong>Decidiram a aprovacao automatica:</strong> {regras.join(', ')}.
      </p>
      {foraDaLista.length > 0 ? (
        <>
          <p>
            <strong>Ficaram de fora</strong> (registradas, nao decidiram a aprovacao
            automatica):
          </p>
          <ul className="flex flex-col gap-1">
            {foraDaLista.map((p) => (
              <li key={p.rule} className="flex flex-col">
                <span>
                  {p.rule}: {rotuloDoResultadoDoMotor(p.outcome)}
                </span>
                <span className="text-2xs text-ink-500">{p.message}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-2xs text-ink-500">Nenhuma regra de fora ficou pendente.</p>
      )}
    </div>
  );
}
